// Renders a stored phone number for display, always with its country code.
//
// COPY. The reference implementation is atm-dmc-api/src/utils/format-phone.ts;
// the third copy is atm-dmc-agent/utils/formatPhone.js. All three must agree with
// the fixture table in formatPhone.test.ts. They are separate repositories, so
// this is deliberate duplication rather than a shared package - see
// atm-dmc-api/docs/superpowers/specs/2026-09-11-phone-display-format-design.md
//
// Display only. Never wrap a value bound to a form input; formatting a field
// while it is being edited fights the picker and corrupts what gets saved.

// Sorted longest-first so 971 wins over 97 and 91. Covers the markets this
// business touches. A number longer than ten digits that matches none of them
// already carries a code we don't know, so it is shown as stored with a leading
// plus rather than put under +91. Only shorter unmatched numbers still fall
// through to +91. Adding a country is a one-line change here and in the two
// sibling copies.
const DIAL_CODES: string[] = [
    '971', '972', '977', '880', '852', '853', '886', '974', '973', '968', '965', '966',
    '91', '66', '65', '60', '44', '61', '86', '81', '82', '49', '33', '31', '39',
    '27', '20', '94', '92', '84', '62', '63',
    '7', '1',
].sort((a, b) => b.length - a.length);

const DEFAULT_DIAL_CODE = '91';

const digitsOnly = (value: unknown): string =>
    typeof value === 'string' || typeof value === 'number'
        ? String(value).replace(/\D/g, '')
        : '';

// booking_has_lead_passenger.phone is a deliberate multi-number field - its label
// is "Guest Contact Numbers" and its validation permits commas. Format each part
// separately. Without this the parts concatenate: "9669060666, 9876543210" becomes
// 96690606669876543210, whose longest-prefix match is 966 (Saudi Arabia), and it
// renders +966 90606669876543210.
export function formatPhone(phone: unknown, countryCode?: unknown): string {
    if (typeof phone === 'string' && /[,/]/.test(phone)) {
        return phone
            .split(/[,/]/)
            .map((part) => formatOne(part, countryCode))
            .filter(Boolean)
            .join(', ');
    }
    return formatOne(phone, countryCode);
}

function formatOne(phone: unknown, countryCode?: unknown): string {
    const allDigits = digitsOnly(phone);
    if (!allDigits) return '';

    let code: string;
    let national: string;

    const explicit = digitsOnly(countryCode);
    if (explicit) {
        // The caller read a record that stores the code separately (users).
        code = explicit;
        national = allDigits;
    } else if (allDigits.length === 10) {
        // A bare national number. Ten digits is the Indian case and by far the
        // most common shape in the older data.
        code = DEFAULT_DIAL_CODE;
        national = allDigits;
    } else {
        const match = DIAL_CODES.find(
            (dial) => allDigits.startsWith(dial) && allDigits.length > dial.length,
        );
        if (!match && allDigits.length > 10) {
            // Too long to be a bare national number, so the code is in there -
            // just not one we know. Prepending +91 would always be wrong.
            return `+${allDigits}`;
        }
        code = match ?? DEFAULT_DIAL_CODE;
        national = match ? allDigits.slice(match.length) : allDigits;
    }

    if (!national) return '';

    // One rule for every country: plus, dial code, space, the national number
    // unsplit. No grouping and no special cases.
    return `+${code} ${national}`;
}
